(async () => {
  if (!customElements.get("product-upsell")) {
    /**
     * Custom element for the product upsell section.
     *
     * Manages a set of tabbed sliders, each powered by a `graphenehc-element`.
     * For each slider, watches the Graphene-rendered `swiper-wrapper` for content
     * and hides the corresponding heading tab if no slides are present.
     */
    class ProductUpsell extends HTMLElement {
      constructor() {
        super();
      }

      connectedCallback() {
        const items = this.querySelectorAll("[data-upsell-item]");

        items.forEach((item) => this._initItem(item));
      }

      /**
       * Initialises visibility watching for a single upsell item.
       * Resolves the item's heading tab via its radio input ID, then either
       * watches the swiper wrapper immediately if Graphene has already rendered,
       * or defers via a MutationObserver until it appears.
       *
       * @param {HTMLElement} item - A upsell element.
       */
      _initItem(item) {
        const input = item.querySelector("input.peer");
        if (!input) return;

        const heading = this.querySelector(`label[for="${input.id}"]`);
        if (!heading) return;

        const grapheneEl = item.querySelector("graphenehc-element");
        if (!grapheneEl) return;

        const wrapper = grapheneEl.querySelector(".swiper-wrapper");

        if (wrapper) {
          this._watchWrapper(wrapper, heading);
        } else {
          // Graphene hasn't rendered yet — wait for the swiper wrapper to appear
          new MutationObserver((_, obs) => {
            const w = grapheneEl.querySelector(".swiper-wrapper");
            if (!w) return;
            obs.disconnect();
            this._watchWrapper(w, heading);
          }).observe(grapheneEl, { childList: true, subtree: true });
        }
      }

      /**
       * Sets initial heading visibility and observes the wrapper for future
       * child changes, updating visibility whenever Graphene adds or removes slides.
       *
       * @param {HTMLElement} wrapper - The `.swiper-wrapper` element to observe.
       * @param {HTMLElement} heading - The heading tab to show or hide.
       */
      _watchWrapper(wrapper, heading) {
        this._updateVisibility(wrapper, heading);
        new MutationObserver(() =>
          this._updateVisibility(wrapper, heading),
        ).observe(wrapper, { childList: true });
      }

      /**
       * Shows or hides the heading tab based on whether the wrapper
       * contains any `.swiper-slide` elements.
       *
       * @param {HTMLElement} wrapper - The `.swiper-wrapper` element to check.
       * @param {HTMLElement} heading - The heading tab to show or hide.
       */
      _updateVisibility(wrapper, heading) {
        heading.style.display = wrapper.querySelector(".swiper-slide")
          ? ""
          : "none";
      }
    }

    window.ProductUpsell = ProductUpsell;
    customElements.define("product-upsell", ProductUpsell);
  }
})();
